/**
 * Renderizador de Markdown mínimo e SEGURO para os manuais (sem dependência e sem HTML cru: tudo vira elementos React,
 * então o texto nunca é interpretado como HTML). Suporta: títulos (#–####), parágrafos, listas (ordenadas, não
 * ordenadas e aninhadas por recuo), tabelas, citações (>), blocos de código (```), linha horizontal e, em linha:
 * `código`, **negrito**, *itálico* e [links](http…|#âncora). Links externos abrem em outra aba com noopener.
 */
import { Fragment, type ReactNode } from "react";

export interface Heading {
  id: string;
  text: string;
  level: number;
}

export function slugify(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const INLINE = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)\s]+\))|(\*[^*\s][^*]*\*)|(__[^_]+__)/;

function stripInline(s: string): string {
  return s.replace(/\*\*([^*]+)\*\*/g, "$1").replace(/`([^`]+)`/g, "$1").replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");
}

export function renderInline(text: string, keyBase = "i"): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let n = 0;
  while (rest) {
    const m = INLINE.exec(rest);
    if (!m) {
      out.push(rest);
      break;
    }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    const tok = m[0];
    const key = `${keyBase}-${n++}`;
    if (m[1]) out.push(<code key={key}>{tok.slice(1, -1)}</code>);
    else if (m[2] || m[5]) out.push(<strong key={key}>{renderInline(tok.slice(2, -2), key)}</strong>);
    else if (m[4]) out.push(<em key={key}>{renderInline(tok.slice(1, -1), key)}</em>);
    else {
      const lm = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok)!;
      const href = lm[2];
      if (/^https?:\/\//i.test(href)) {
        out.push(
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {renderInline(lm[1], key)}
          </a>,
        );
      } else if (href.startsWith("#")) {
        out.push(
          <a key={key} href={href}>
            {renderInline(lm[1], key)}
          </a>,
        );
      } else out.push(<Fragment key={key}>{renderInline(lm[1], key)}</Fragment>); // link relativo: só o texto
    }
    rest = rest.slice(m.index + tok.length);
  }
  return out;
}

type Block =
  | { t: "h"; level: number; text: string }
  | { t: "p"; text: string }
  | { t: "code"; text: string }
  | { t: "quote"; lines: string[] }
  | { t: "table"; head: string[]; rows: string[][] }
  | { t: "hr" }
  | { t: "list"; items: ListItem[]; ordered: boolean };

interface ListItem {
  text: string;
  children: Block | null; // sublista
}

const BULLET = /^(\s*)([-*]|\d+[.)])\s+(.*)$/;

function cells(line: string): string[] {
  return line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
}

function parseList(lines: string[], start: number): [Block, number] {
  const first = BULLET.exec(lines[start])!;
  const baseIndent = first[1].length;
  const ordered = /\d/.test(first[2]);
  const items: ListItem[] = [];
  let i = start;
  while (i < lines.length) {
    const m = BULLET.exec(lines[i]);
    if (!m || m[1].length < baseIndent) break;
    if (m[1].length > baseIndent) {
      // sublista do item anterior
      const [sub, next] = parseList(lines, i);
      if (items.length) items[items.length - 1].children = sub;
      i = next;
      continue;
    }
    const item: ListItem = { text: m[3], children: null };
    i++;
    // continuação do texto do item (linhas recuadas que não são marcadores)
    while (i < lines.length && lines[i].trim() && !BULLET.test(lines[i]) && /^\s{2,}\S/.test(lines[i])) {
      item.text += " " + lines[i].trim();
      i++;
    }
    items.push(item);
  }
  return [{ t: "list", items, ordered }, i];
}

export function parseMarkdown(src: string): Block[] {
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }
    if (line.startsWith("```")) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) buf.push(lines[i++]);
      i++;
      blocks.push({ t: "code", text: buf.join("\n") });
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      blocks.push({ t: "h", level: h[1].length, text: h[2].trim() });
      i++;
      continue;
    }
    if (/^(-{3,}|\*{3,})\s*$/.test(line)) {
      blocks.push({ t: "hr" });
      i++;
      continue;
    }
    if (line.startsWith(">")) {
      const buf: string[] = [];
      while (i < lines.length && lines[i].startsWith(">")) buf.push(lines[i++].replace(/^>\s?/, ""));
      blocks.push({ t: "quote", lines: buf });
      continue;
    }
    if (line.trim().startsWith("|") && i + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1])) {
      const head = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) rows.push(cells(lines[i++]));
      blocks.push({ t: "table", head, rows });
      continue;
    }
    if (BULLET.test(line)) {
      const [b, next] = parseList(lines, i);
      blocks.push(b);
      i = next;
      continue;
    }
    const buf = [line.trim()];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^(#{1,4}\s|```|>|\||(-{3,}|\*{3,})\s*$)/.test(lines[i]) &&
      !BULLET.test(lines[i])
    ) {
      buf.push(lines[i++].trim());
    }
    blocks.push({ t: "p", text: buf.join(" ") });
  }
  return blocks;
}

export function headingsOf(src: string, maxLevel = 2): Heading[] {
  return parseMarkdown(src)
    .filter((b): b is Extract<Block, { t: "h" }> => b.t === "h" && b.level >= 2 && b.level <= maxLevel)
    .map((b) => ({ id: slugify(stripInline(b.text)), text: stripInline(b.text), level: b.level }));
}

function renderBlock(b: Block, key: string): ReactNode {
  switch (b.t) {
    case "h": {
      const id = b.level >= 2 ? slugify(stripInline(b.text)) : undefined;
      const Tag = `h${Math.max(2, Math.min(b.level, 4))}` as "h2"; // a página já tem o h1
      return (
        <Tag key={key} id={id}>
          {renderInline(b.text, key)}
        </Tag>
      );
    }
    case "p":
      return <p key={key}>{renderInline(b.text, key)}</p>;
    case "code":
      return (
        <pre key={key} className="md-code">
          <code>{b.text}</code>
        </pre>
      );
    case "quote":
      return <blockquote key={key}>{renderInline(b.lines.join(" "), key)}</blockquote>;
    case "hr":
      return <hr key={key} />;
    case "table":
      return (
        <div key={key} className="md-table-wrap">
          <table className="md-table">
            <thead>
              <tr>
                {b.head.map((c, j) => (
                  <th key={j}>{renderInline(c, `${key}h${j}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((r, ri) => (
                <tr key={ri}>
                  {r.map((c, j) => (
                    <td key={j}>{renderInline(c, `${key}r${ri}c${j}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "list": {
      const L = b.ordered ? "ol" : "ul";
      return (
        <L key={key}>
          {b.items.map((it, j) => (
            <li key={j}>
              {renderInline(it.text, `${key}-${j}`)}
              {it.children && renderBlock(it.children, `${key}-${j}s`)}
            </li>
          ))}
        </L>
      );
    }
  }
}

export function Markdown({ source, skipTitle = false }: { source: string; skipTitle?: boolean }) {
  let blocks = parseMarkdown(source);
  if (skipTitle && blocks[0]?.t === "h" && blocks[0].level === 1) blocks = blocks.slice(1);
  return <div className="md">{blocks.map((b, i) => renderBlock(b, `b${i}`))}</div>;
}
